import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Alert,
  Form,
  Modal,
  Spinner,
  Dropdown,
} from "react-bootstrap";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "./AuthContext";
import { apiFetch } from "./apiFetch";

interface ApiToken {
  id: string;
  user_id: string;
  name: string;
  token: string;
  created_at: string;
  is_active: boolean;
}

interface UserStats {
  usage: number;
  tier: "free" | "plus";
  maxRate: number;
}

export default function ApiTokens() {
  const { user, getIdToken } = useAuth();
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [stats, setStats] = useState<UserStats>({
    usage: 0,
    tier: "free",
    maxRate: 0,
  });

  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTokenName, setNewTokenName] = useState("");
  const [creating, setCreating] = useState(false);
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadTokens();
    }
  }, [user]);

  const loadTokens = async () => {
    try {
      setLoading(true);

      const token = await getIdToken();
      if (!token) {
        console.error("No ID token available");
        return;
      }

      const response = await apiFetch("/user/tokens", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTokens(data.tokens);
        setStats({
          usage: data.usage,
          tier: data.tier,
          maxRate: data.maxRate,
        });
      } else {
        console.error("Failed to load tokens");
      }
    } catch (error) {
      console.error("Error loading tokens:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateNewToken = async () => {
    if (!newTokenName.trim()) {
      alert("Please enter a token name");
      return;
    }

    try {
      setCreating(true);

      const token = await getIdToken();
      if (!token) {
        alert("Authentication required");
        return;
      }

      const response = await apiFetch("/user/tokens", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newTokenName,
        }),
      });

      if (response.ok) {
        setShowCreateModal(false);
        setNewTokenName("");
        await loadTokens();
      } else {
        const error = await response.json();
        alert(`Failed to create token: ${error.error}`);
      }
    } catch (error) {
      console.error("Error creating token:", error);
      alert("Failed to create token - check console for details");
    } finally {
      setCreating(false);
    }
  };

  const revokeToken = async (tokenId: string) => {
    if (
      !confirm(
        "Are you sure you want to revoke this token? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      const token = await getIdToken();
      if (!token) {
        alert("Authentication required");
        return;
      }

      const response = await apiFetch(`/user/tokens/${tokenId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await loadTokens();
      } else {
        const error = await response.json();
        alert(`Failed to revoke token: ${error.error}`);
      }
    } catch (error) {
      console.error("Error revoking token:", error);
      alert("Failed to revoke token");
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString();
  };

  const copyToClipboard = (text: string, tokenId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedTokenId(tokenId);
    setTimeout(() => setCopiedTokenId(null), 2000);
  };

  if (!user) {
    return (
      <Container className="py-5">
        <Row>
          <Col md={12}>
            <Card className="text-center">
              <Card.Body>
                <h3>Please sign in to manage your API tokens</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Helmet>
        <title>API Tokens - Headliners News API Dashboard</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Row>
        <Col md={12}>
          <Card className="shadow border-0">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <h2 className="fw-bold text-primary mb-0">API Tokens</h2>
                    <span
                      className={`badge ${
                        stats.tier === "plus"
                          ? "bg-info"
                          : "bg-secondary"
                      }`}
                    >
                      {stats.tier.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-muted mb-0">
                    Manage your Headline Feed API access tokens
                  </p>
                </div>
                <Button
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  Generate New Token
                </Button>
              </div>

              {/* Usage Statistics */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="bg-light border-0">
                    <Card.Body className="text-center">
                      <h4 className="fw-bold text-primary">
                        {tokens.filter((t) => t.is_active).length}
                      </h4>
                      <small className="text-muted">Active Tokens</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="bg-light border-0">
                    <Card.Body className="text-center">
                      <h4 className="fw-bold text-secondary">
                        {tokens.filter((t) => !t.is_active).length}
                      </h4>
                      <small className="text-muted">Revoked Tokens</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="bg-light border-0">
                    <Card.Body className="text-center">
                      <h4
                        className={`fw-bold ${
                          stats.usage / stats.maxRate > 0.8
                            ? "text-danger"
                            : stats.usage / stats.maxRate > 0.6
                              ? "text-warning"
                              : "text-success"
                        }`}
                      >
                        {stats.usage}/{stats.maxRate}
                      </h4>
                      <small className="text-muted">Requests Today</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="bg-light border-0">
                    <Card.Body className="text-center">
                      <h4 className="fw-bold text-info">{tokens.length}</h4>
                      <small className="text-muted">Total Tokens</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Alert variant="info" className="mb-4">
                <strong>Security Notice:</strong> Keep your API tokens secure
                and never share them publicly. Each token provides full access
                to your account data.
              </Alert>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : tokens.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">
                    No API tokens found. Generate your first token to get
                    started.
                  </p>
                </div>
              ) : (
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Token</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokens.map((token) => (
                      <tr
                        key={token.id}
                        className={
                          !token.is_active ? "table-secondary opacity-75" : ""
                        }
                      >
                        <td
                          className={`fw-bold ${!token.is_active ? "text-muted" : ""}`}
                        >
                          {token.name}
                          {!token.is_active && (
                            <span className="ms-2 badge bg-secondary">
                              Revoked
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <code
                              className={`px-2 py-1 rounded me-2 ${token.is_active ? "bg-light" : "bg-secondary text-white"}`}
                            >
                              {token.token.substring(0, 20)}...
                            </code>
                            {token.is_active && (
                              <div className="d-flex align-items-center">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() =>
                                    copyToClipboard(token.token, token.id)
                                  }
                                >
                                  Copy
                                </Button>
                                <Dropdown
                                  show={copiedTokenId === token.id}
                                  className="ms-2"
                                >
                                  <Dropdown.Toggle
                                    variant="link"
                                    size="sm"
                                    className="p-0 border-0 shadow-none"
                                    style={{ pointerEvents: "none" }}
                                  ></Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item
                                      disabled
                                      className="text-success"
                                    >
                                      Copied!
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge ${token.is_active ? "bg-success" : "bg-secondary"}`}
                          >
                            {token.is_active ? "Active" : "Revoked"}
                          </span>
                        </td>
                        <td className={!token.is_active ? "text-muted" : ""}>
                          {formatDate(token.created_at)}
                        </td>
                        <td>
                          {token.is_active ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => revokeToken(token.id)}
                            >
                              Revoke
                            </Button>
                          ) : (
                            <small className="text-muted">Revoked</small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <div className="mt-4">
                <h5 className="fw-bold">Usage Instructions</h5>
                <p className="text-muted">
                  Include your API token in the request headers as:
                  <code className="bg-light px-2 py-1 rounded ms-2">
                    Authorization: Bearer YOUR_TOKEN
                  </code>
                </p>
                <p className="text-muted">Example API call:</p>
                <pre className="bg-light p-3 rounded">
                  {`curl -X POST -H "Authorization: Bearer YOUR_TOKEN" \\
     -H "Content-Type: application/json" \\
     -d '{"title": "trUMp"}' \\
     https://api.headlinefeed.dev/api/search`}
                </pre>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Token Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New API Token</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              generateNewToken();
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label>Token Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Production API, Development API"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !creating) {
                    e.preventDefault();
                    generateNewToken();
                  }
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={generateNewToken}
            disabled={creating}
          >
            {creating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              "Create Token"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
